import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const CONSENT_STORAGE_KEY = "lgpdConsent";
const CONSENT_VERSION = "1.0.0";

const defaultPreferences = {
  essential: true,
  analytics: false,
  marketing: false,
};

const ConsentContext = createContext({
  preferences: defaultPreferences,
  acceptedAt: null,
  version: CONSENT_VERSION,
  setPreferences: () => {},
  acceptAll: () => {},
  rejectNonEssential: () => {},
  openManager: () => {},
  closeManager: () => {},
});

export function ConsentProvider({ children }) {
  const [preferences, setPreferences] = useState(defaultPreferences);
  const [acceptedAt, setAcceptedAt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [managerOpen, setManagerOpen] = useState(false);

  // Load saved consent
  useEffect(() => {
    try {
      const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.version === CONSENT_VERSION && parsed.preferences) {
          setPreferences({ ...defaultPreferences, ...parsed.preferences });
          setAcceptedAt(parsed.acceptedAt || null);
          setShowBanner(false);
          return;
        }
      }
      setShowBanner(true);
    } catch {
      setShowBanner(true);
    }
  }, []);

  const persist = (prefs) => {
    const payload = {
      version: CONSENT_VERSION,
      preferences: prefs,
      acceptedAt: new Date().toISOString(),
    };
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(payload));
    setAcceptedAt(payload.acceptedAt);
  };

  const acceptAll = () => {
    const prefs = { essential: true, analytics: true, marketing: true };
    setPreferences(prefs);
    persist(prefs);
    setShowBanner(false);
    setManagerOpen(false);
    document.body.dataset.consent = "all";
  };

  const rejectNonEssential = () => {
    const prefs = { essential: true, analytics: false, marketing: false };
    setPreferences(prefs);
    persist(prefs);
    setShowBanner(false);
    setManagerOpen(false);
    document.body.dataset.consent = "essential";
  };

  const openManager = () => setManagerOpen(true);
  const closeManager = () => setManagerOpen(false);

  // Expose a global hook for scripts to check consent
  useEffect(() => {
    window.__lgpdConsent = {
      get: () => ({ preferences, acceptedAt, version: CONSENT_VERSION }),
    };
  }, [preferences, acceptedAt]);

  const value = useMemo(
    () => ({
      preferences,
      acceptedAt,
      version: CONSENT_VERSION,
      setPreferences,
      acceptAll,
      rejectNonEssential,
      openManager,
      closeManager,
    }),
    [preferences, acceptedAt]
  );

  return (
    <ConsentContext.Provider value={value}>
      {children}
      {showBanner && (
        <ConsentBanner
          onManage={() => setManagerOpen(true)}
          onAcceptAll={acceptAll}
          onReject={rejectNonEssential}
        />
      )}
      {managerOpen && (
        <ConsentManager
          preferences={preferences}
          setPreferences={(p) => setPreferences(p)}
          onSave={() => {
            persist(preferences);
            setManagerOpen(false);
            setShowBanner(false);
          }}
          onCancel={closeManager}
        />
      )}
    </ConsentContext.Provider>
  );
}

export function useConsent() {
  return useContext(ConsentContext);
}

function ConsentBanner({ onManage, onAcceptAll, onReject }) {
  return (
    <div className="consent-banner" role="dialog" aria-live="polite">
      <div className="consent-banner__text">
        Utilizamos cookies essenciais para o funcionamento do site e, com seu
        consentimento, cookies de análise e marketing para melhorar sua
        experiência. Consulte a{" "}
        <a href="/politica-cookies" className="link">
          Política de Cookies
        </a>{" "}
        e a{" "}
        <a href="/politica-privacidade" className="link">
          Política de Privacidade
        </a>
        . Leia também o{" "}
        <a href="/aceite-ciencia" className="link">
          Aceite e Ciência
        </a>
        .
      </div>
      <div className="consent-banner__actions">
        <button className="btn-secondary" onClick={onReject}>
          Recusar não essenciais
        </button>
        <button className="btn-secondary" onClick={onManage}>
          Configurar
        </button>
        <button className="btn-primary" onClick={onAcceptAll}>
          Aceitar todos
        </button>
      </div>
    </div>
  );
}

function ConsentManager({ preferences, setPreferences, onSave, onCancel }) {
  const update = (key) => (e) =>
    setPreferences({ ...preferences, [key]: e.target.checked });
  return (
    <div className="consent-modal" role="dialog" aria-modal="true">
      <div className="consent-modal__card" onClick={(e) => e.stopPropagation()}>
        <h3 className="mt-0">Preferências de Cookies</h3>
        <p className="text-muted">
          Gerencie seu consentimento conforme a LGPD. Cookies essenciais são
          sempre ativos.
        </p>
        <div className="consent-list">
          <label className="consent-item">
            <input type="checkbox" checked={true} disabled /> Essenciais —
            obrigatórios para funcionamento do site
          </label>
          <label className="consent-item">
            <input
              type="checkbox"
              checked={preferences.analytics}
              onChange={update("analytics")}
            />{" "}
            Análise — ajuda a entender o uso do site
          </label>
          <label className="consent-item">
            <input
              type="checkbox"
              checked={preferences.marketing}
              onChange={update("marketing")}
            />{" "}
            Marketing — conteúdo e ofertas relevantes
          </label>
        </div>
        <div className="consent-modal__actions">
          <button className="btn-secondary" onClick={onCancel}>
            Cancelar
          </button>
          <button className="btn-primary" onClick={onSave}>
            Salvar preferências
          </button>
        </div>
        <p className="text-muted small">
          Você pode alterar seu consentimento a qualquer momento em
          “Preferências de Cookies” no rodapé.
        </p>
      </div>
    </div>
  );
}
